'use client';

import { useState } from 'react';
import { INTL_CONSENT_ITEMS, IntlConsentKey } from '@/lib/recruitment/intl-application';
import { FieldLabel, TextInput } from '@/components/recruitment/intl/fields';

export type SubmitApplicationResult = { ok: boolean; missing?: string[]; error?: string };

export function ConsentStep({
  items = INTL_CONSENT_ITEMS,
  onSubmit,
  onBack,
}: {
  /** The statements as worded in Recruitment -> Job Applications (code defaults otherwise). */
  items?: readonly { key: string; text: string }[];
  /** POST the final submit (the wizard owns the fetch + confirmation state). */
  onSubmit: (
    consents: Record<IntlConsentKey, boolean>,
    signatureFullName: string
  ) => Promise<SubmitApplicationResult>;
  onBack: (() => void) | null;
}) {
  const [consents, setConsents] = useState<Record<IntlConsentKey, boolean>>({
    consent_data_processing: false,
    consent_recorded_interviews: false,
    consent_accuracy: false,
  });
  const [signature, setSignature] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState('');
  const [serverMissing, setServerMissing] = useState<string[]>([]);

  const allChecked = items.every((item) => consents[item.key as IntlConsentKey]);
  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const handleSubmit = async () => {
    setServerError('');
    setServerMissing([]);
    if (!allChecked || !signature.trim()) {
      setShowErrors(true);
      return;
    }
    setSubmitting(true);
    try {
      const result = await onSubmit(consents, signature.trim());
      if (!result.ok) {
        if (result.missing?.length) setServerMissing(result.missing);
        if (result.error) setServerError(result.error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-4 space-y-4">
        {items.map((item) => (
          <label key={item.key} className="flex items-start gap-3 cursor-pointer">
            <button
              type="button"
              role="checkbox"
              aria-checked={consents[item.key as IntlConsentKey]}
              onClick={() =>
                setConsents((c) => ({ ...c, [item.key]: !c[item.key as IntlConsentKey] }))
              }
              className={`w-5 h-5 rounded flex-shrink-0 mt-0.5 border flex items-center justify-center transition-colors ${
                consents[item.key as IntlConsentKey]
                  ? 'bg-emerald-600 border-emerald-600'
                  : showErrors && !consents[item.key as IntlConsentKey]
                    ? 'border-red-500 bg-gray-800'
                    : 'border-gray-600 bg-gray-800'
              }`}
            >
              {consents[item.key as IntlConsentKey] && (
                <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </button>
            <span className="text-sm text-gray-300 leading-relaxed">{item.text}</span>
          </label>
        ))}
        {showErrors && !allChecked && (
          <p className="text-xs text-red-400 flex items-center gap-1">
            <span>⚠</span> Please accept all three statements to submit your application.
          </p>
        )}
      </div>

      <div>
        <FieldLabel label="Electronic Signature - Full Name" required />
        <TextInput
          value={signature}
          onChange={setSignature}
          placeholder="Type your full legal name"
          hasError={showErrors && !signature.trim()}
        />
        {showErrors && !signature.trim() && (
          <p className="mt-1 text-xs text-red-400 flex items-center gap-1">
            <span>⚠</span> Please type your full name as your electronic signature.
          </p>
        )}
        <p className="mt-1.5 text-xs text-gray-500">Date: {today}</p>
      </div>

      {(serverError || serverMissing.length > 0) && (
        <div className="bg-red-900/30 border border-red-700 text-red-300 rounded-xl px-4 py-3 text-sm">
          <p>{serverError || 'The application is not complete yet.'}</p>
          {serverMissing.length > 0 && (
            <ul className="mt-2 space-y-1">
              {serverMissing.map((label) => (
                <li key={label} className="text-xs flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" />
                  {label}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="flex gap-3 pt-1">
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="px-5 py-3.5 rounded-xl bg-gray-800 hover:bg-gray-700 border border-gray-700 text-white text-sm font-medium transition-colors"
          >
            Back
          </button>
        )}
        <button
          type="button"
          onClick={() => void handleSubmit()}
          disabled={submitting}
          className="btn btn-primary flex-1 py-3.5 font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60 h-auto"
        >
          {submitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Submitting…
            </>
          ) : (
            'Submit Application'
          )}
        </button>
      </div>
    </div>
  );
}
